/**
 * Coach panel for an Academy lesson, over the bottom-left of the display: the current step's
 * text, a three-level help drawer, and Continue / Try again / Next lesson.
 * It only renders; the game session drives the lesson run and pauses the sim between steps.
 *
 * Help is deliberately staged. Level 1 says what to decide, level 2 says it on the radio the way
 * the voice path accepts it, and level 3 types it for you. The level reached is reported back, so
 * a lesson passed unaided can be told apart from one read off the screen.
 */
import { COMMAND_CATALOG } from '@atc/sim';
import type { Lesson, LessonRun } from './lesson';
import type { AssistLevel } from './progress';

export interface CoachHandlers {
  /** Title of the lesson after this one, if any. */
  nextTitle?: string;
  /**
   * Push to talk: whether it is on, and whether it may be switched on from here. Level 2 help is
   * the call as it is spoken, so it is also where a player finds out they can say it.
   */
  speech?: { enabled(): boolean; allowed: boolean; onChange(on: boolean): void };
  onContinue(): void;
  onShowMe(commands: string[]): void;
  onRetry(): void;
  onExit(): void;
  onNext(): void;
  onWatch?(): void;
  /** Offered when this pass finishes the beginner tier or the whole route: a first real shift. */
  onShift?(): void;
}

export interface CoachPanel {
  render(run: LessonRun): void;
  /** The deepest help level used so far, across every step of this attempt. */
  assist(): AssistLevel;
  destroy(): void;
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export function createCoachPanel(container: HTMLElement, lesson: Lesson, h: CoachHandlers): CoachPanel {
  const el = document.createElement('section');
  el.className = 'coach';
  el.setAttribute('aria-live', 'polite');
  el.setAttribute('aria-label', `${lesson.id === 'tutorial' ? 'Tutorial' : 'Lesson'}: ${lesson.title}`);
  container.appendChild(el);
  let current: LessonRun | null = null;
  /** Help level revealed on the current step, and the worst reached in this attempt. */
  let level = 0, deepest: AssistLevel = 0;
  let levelFor = -1;
  let phrases = false;

  /** What this lesson lets you say, for the "what can I say" drawer. */
  const catalog = lesson.commands.map((id) => COMMAND_CATALOG.find((c) => c.id === id)).filter((c): c is NonNullable<typeof c> => !!c);

  function reveal(next: 1 | 2 | 3): void {
    if (!current) return;
    if (levelFor !== current.stepIndex) { levelFor = current.stepIndex; level = 0; }
    level = Math.max(level, next);
    deepest = Math.max(deepest, next) as AssistLevel;
    render(current);
  }

  el.addEventListener('click', (e) => {
    const act = (e.target as HTMLElement).closest<HTMLElement>('[data-act]')?.dataset.act;
    if (!act || !current) return;
    if (act === 'hint') reveal(1);
    else if (act === 'say') reveal(2);
    else if (act === 'show') {
      const commands = current.step?.solution ?? [];
      if (commands.length) { reveal(3); h.onShowMe(commands); }
    } else if (act === 'mic') { h.speech?.onChange(true); render(current); }
    else if (act === 'phrases') { phrases = !phrases; render(current); }
    else if (act === 'continue') h.onContinue();
    else if (act === 'retry') h.onRetry();
    else if (act === 'next') h.onNext();
    else if (act === 'watch') h.onWatch?.();
    else if (act === 'exit') h.onExit();
    else if (act === 'shift') h.onShift?.();
  });

  function helpBody(run: LessonRun): string {
    const step = run.step;
    if (!step) return '';
    const shown = levelFor === run.stepIndex ? level : 0;
    const out: string[] = [];
    if (shown >= 1) out.push(`<p class="coach-hint"><span>Hint</span>${esc(step.hint)}</p>`);
    if (shown >= 2) out.push(`<p class="coach-say"><span>Say</span>“${esc(step.say)}”</p>`);
    if (shown >= 2 && h.speech) {
      out.push(`<p class="coach-mic"><span>Radio</span>${h.speech.enabled() ? 'Click the aircraft, hold Space, and say it.'
        : h.speech.allowed ? '<button class="btn" type="button" data-act="mic">Turn on push to talk</button>'
          : 'Push to talk is off in this game configuration.'}</p>`);
    }
    if (shown >= 3 && step.solution.length) out.push(`<p class="coach-typed"><span>Type</span>${step.solution.map((c) => `<code>${esc(c)}</code>`).join(' then ')}</p>`);
    if (phrases && catalog.length) {
      out.push(`<div class="coach-phrases"><h4>What you can say here</h4><dl>${catalog.map((c) => `<dt>${esc(c.title)}</dt><dd>“${esc(c.spoken)}” · <code>${esc(c.syntax)}</code></dd>`).join('')}</dl></div>`);
    }
    return out.join('');
  }

  function helpActions(run: LessonRun): string {
    const step = run.step;
    const shown = levelFor === run.stepIndex ? level : 0;
    if (lesson.id === 'tutorial') return [
      shown < 1 ? '<button class="btn" type="button" data-act="hint">Hint</button>' : '',
      catalog.length ? `<button class="btn coach-more" type="button" data-act="phrases" aria-expanded="${phrases}">Radio phrases</button>` : '',
      '<button class="btn" type="button" data-act="exit">Leave tutorial</button>',
    ].join('');
    return [
      shown < 1 ? '<button class="btn" type="button" data-act="hint">Hint</button>' : '',
      shown < 2 ? '<button class="btn" type="button" data-act="say">Say it</button>' : '',
      step?.solution.length ? '<button class="btn" type="button" data-act="show">Show me</button>' : '',
      catalog.length ? `<button class="btn coach-more" type="button" data-act="phrases" aria-expanded="${phrases}">What can I say?</button>` : '',
      '<button class="btn" type="button" data-act="exit">Leave lesson</button>',
    ].join('');
  }

  function render(run: LessonRun): void {
    const focusedAction = el.contains(document.activeElement) ? (document.activeElement as HTMLElement).dataset.act : undefined;
    current = run;
    const steps = run.script.steps, i = run.stepIndex, step = run.step;
    const dots = steps.map((_, k) => {
      const done = run.status === 'complete' || k < i || (k === i && run.status === 'passed');
      return `<span class="${done ? 'done' : k === i ? 'current' : ''}"></span>`;
    }).join('');
    let title = '', body = '', actions = '';
    switch (run.status) {
      case 'running':
        title = step?.title ?? '';
        body = `<p>${esc(step?.text ?? '')}</p>${helpBody(run)}`;
        actions = helpActions(run);
        break;
      case 'passed': {
        const last = i === steps.length - 1;
        title = `${step?.title ?? ''}: done`;
        // A step the player had already settled explains itself, so working ahead never looks like a glitch.
        const note = run.passNote ? `<p class="coach-note">${esc(run.passNote)}</p>` : '';
        body = `${note}<p>${last ? 'That was the last step.' : `Next: ${esc(steps[i + 1]!.title)}.`} The sim waits until you continue.</p>`;
        actions = `<button class="btn-primary" type="button" data-act="continue">${last ? lesson.id === 'tutorial' ? 'Finish tutorial' : 'Finish lesson' : 'Continue'}</button>`;
        break;
      }
      case 'failed':
        title = 'Reset the picture. Try again.';
        body = `<p>${esc(run.failText)}</p><p>The exercise restarts with the original traffic. Take your time and ask the coach for a hint.</p>`;
        actions = `<button class="btn-primary" type="button" data-act="retry">Try again</button><button class="btn" type="button" data-act="exit">${lesson.id === 'tutorial' ? 'Main menu' : 'Back to Academy'}</button>`;
        break;
      case 'complete':
        title = lesson.id === 'tutorial' ? 'Tutorial complete' : 'Lesson complete';
        body = lesson.id === 'tutorial'
          ? '<p class="coach-result">✓ Arrival landed. Runway cleared. Departure airborne.</p><p>You can now choose an airport and start a watch, or replay this traffic.</p>'
          : `<p class="coach-result">✓ ${steps.length} steps passed${deepest === 0 ? ' without assistance' : ' with coach support'}.</p><p>${h.onShift ? 'You have the basics. A short shift with light traffic puts them to work; the Academy keeps your place.' : h.nextTitle ? `Up next: ${esc(h.nextTitle)}.` : 'That was the last lesson. Try a timed shift from the main menu.'}</p>`;
        actions = lesson.id === 'tutorial'
          ? '<button class="btn-primary" type="button" data-act="watch">Start a watch</button><button class="btn" type="button" data-act="retry">Replay tutorial</button><button class="btn" type="button" data-act="exit">Main menu</button>'
          : h.onShift
          ? `<button class="btn-primary" type="button" data-act="shift">Try a real shift</button>${h.nextTitle ? '<button class="btn" type="button" data-act="next">Next lesson</button>' : ''}<button class="btn" type="button" data-act="exit">Back to Academy</button>`
          : h.nextTitle
          ? '<button class="btn-primary" type="button" data-act="next">Next lesson</button><button class="btn" type="button" data-act="exit">Back to Academy</button>'
          : '<button class="btn-primary" type="button" data-act="exit">Back to Academy</button>';
        break;
    }
    el.className = `coach ${run.status}`;
    el.innerHTML = `
      <header class="coach-head"><span>${esc(lesson.title)}</span><span class="coach-dots" role="img" aria-label="Step ${Math.min(i + 1, steps.length)} of ${steps.length}">${dots}</span></header>
      <p class="coach-step-label">${run.status === 'complete' ? lesson.id === 'tutorial' ? 'Tutorial debrief' : 'Exercise debrief' : `Step ${Math.min(i + 1, steps.length)} of ${steps.length} / ${run.status === 'passed' ? 'Complete · Traffic paused' : run.status === 'failed' ? 'Review and retry' : 'Your next decision'}`}</p>
      <h3 class="coach-title">${esc(title)}</h3>
      <div class="coach-text">${body}</div>
      <div class="coach-actions">${actions}</div>`;
    if (run.status !== 'running') el.querySelector<HTMLElement>('.btn-primary')?.focus();
    else if (focusedAction) (el.querySelector<HTMLElement>(`[data-act="${focusedAction}"]`) ?? el.querySelector<HTMLElement>('.coach-actions button'))?.focus();
    if (lesson.id === 'tutorial' && run.status !== 'running') el.scrollIntoView({ block: 'nearest' });
  }

  return { render, assist: () => deepest, destroy: () => el.remove() };
}
